import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { lastValueFrom } from 'rxjs';

import { Logging } from '../utils/logging';

@Injectable({
  providedIn: 'root',
})
export class BaseApiService {
  token: string;
  user: string = '';
  constructor(private httpClient: HttpClient) {}

  set headerToken(value: string) {
    this.token = value;
  }

  set userToken(value: string) {
    this.user = value;
  }

  get headers() {
    const token: string = this.token;
    const user: string = this.user;
    return {
      headers: new HttpHeaders()
        .set('Authorization', 'Bearer ' + token)
        .set('Content-Type', 'application/json')
        .set('user', user),
    };
  }

  post<T>(uri: string, requestObject: T) {
    return lastValueFrom(
      this.httpClient
        .post<T>(uri, JSON.stringify(requestObject), this.headers)
        .pipe(
          tap({
            next: async (_: T) => {},
            error: err => {
              this.handleError(err);
            },
          }),
        ),
    );
  }

  putVoid<T>(uri: string, requestObject: T) {
    lastValueFrom(
      this.httpClient
        .put<T>(uri, JSON.stringify(requestObject), this.headers)
        .pipe(
          tap({
            next: async (_: any) => {},
            error: err => {
              this.handleError(err);
            },
          }),
        ),
    );
  }

  postVoid<T>(uri: string, requestObject: T) {
    lastValueFrom(
      this.httpClient
        .post<T>(uri, JSON.stringify(requestObject), this.headers)
        .pipe(
          tap({
            next: async (_: any) => {},
            error: err => {
              this.handleError(err);
            },
          }),
        ),
    );
  }

  postTU<T, U>(uri: string, requestObject: T) {
    return lastValueFrom(
      this.httpClient
        .post<U>(uri, JSON.stringify(requestObject), this.headers)
        .pipe(
          tap({
            next: async (_: U) => {},
            error: err => {
              this.handleError(err);
            },
          }),
        ),
    );
  }

  postFile<U>(uri: string, requestObject: any) {
    const options = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.token,
      }),
    };
    return lastValueFrom(
      this.httpClient.post(uri, requestObject, options).pipe(
        tap({
          next: async (_: U) => {},
          error: err => {
            this.handleError(err);
          },
        }),
      ),
    );
  }

  getTU<T>(uri: string, incomingParams?: any) {
    let safeParams = {
      ...incomingParams,
    };
    Object.entries(incomingParams ?? {}).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        delete safeParams[key];
      }
    });
    return lastValueFrom(
      this.httpClient
        .get<T>(uri, { params: safeParams, headers: this.headers.headers })
        .pipe(
          tap({
            next: async (_: T) => {},
            error: err => {
              this.handleError(err);
            },
          }),
        ),
    );
  }

  get<T>(uri: string) {
    return lastValueFrom(
      this.httpClient.get<T>(uri, this.headers).pipe(
        tap({
          next: async (_: T) => {},
          error: err => {
            this.handleError(err);
          },
        }),
      ),
    );
  }

  async downloadFile(uri: string) {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.token,
    });

    try {
      const response = await lastValueFrom(
        this.httpClient.get(uri, {
          observe: 'response',
          responseType: 'blob',
          headers: headers,
        }),
      );
      const blob = response.body;
      const filename = this.parseContentDispositionForFilename(
        response.headers,
      );

      this.jankyBlobObjectDownloadWorkAround(blob, filename);
    } catch (error) {
      Logging.error('Error downloading file:', error);
    }
  }

  delete<T>(uri: string) {
    return lastValueFrom(
      this.httpClient.delete<T>(uri, this.headers).pipe(
        tap({
          next: async (_: T) => {},
          error: err => {
            this.handleError(err);
          },
        }),
      ),
    );
  }

  private parseContentDispositionForFilename(headers: HttpHeaders) {
    const disposition = headers.get('content-disposition');
    const filenamePropStart = disposition.indexOf('filename=');
    const filenamePropEnd = disposition.indexOf(';', filenamePropStart);
    let filename = disposition.slice(
      filenamePropStart + 'filename='.length,
      filenamePropEnd,
    );
    if (filename.startsWith('"')) {
      filename = filename.substring(1);
    }
    if (filename.endsWith('"')) {
      filename = filename.substring(0, filename.length - 1);
    }

    return filename;
  }

  private jankyBlobObjectDownloadWorkAround(blob: Blob, filename: string) {
    const temporaryBlobUrl = window.URL.createObjectURL(blob);
    const temporaryLinkForDownload = document.createElement('a');
    temporaryLinkForDownload.href = temporaryBlobUrl;
    temporaryLinkForDownload.download = filename;
    document.body.appendChild(temporaryLinkForDownload);
    temporaryLinkForDownload.click();
    temporaryLinkForDownload.remove();
    window.URL.revokeObjectURL(temporaryBlobUrl); // Clean up the URL
  }

  private handleError(error: Response) {
    Logging.error(error);
  }
}
